import { GetServerSideProps } from 'next';
import { useParams } from 'next/navigation';
import { useContext, useEffect } from 'react';

import PacksList from '@/components/app/cards/PacksList';
import { PacksContext } from '@/components/app/layout/PacksProvider';
import Layout from '@/components/layout/LayoutWrapper';
import { usePacksFeed } from '@/hooks/app/usePacksFeed';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { getInitialServerState } from '@/utils/pageGeneration/getServerSideProps';

const PacksFeed = () => {
	const params = useParams();
	const routerPush = useRouterPush();
	const { setPageTitle, setPageSubtitle } = useContext(PacksContext);
	const packFeedName = typeof params?.feedName === 'string' ? params.feedName : undefined;
	const {
		packs,
		isLoading,
		title: feedTitle,
		subtitle: feedSubtitle,
		error,
		hasResponse,
		notFound,
	} = usePacksFeed(packFeedName);

	// Two shapes leave `isLoading` false with nothing confirmed: a malformed 200 body (no
	// error, but nothing usable either), and a network-level failure (a rejected request
	// with no HTTP status at all — DNS, timeout, reset). Both are unconfirmed, so treat them
	// as still-loading everywhere `isLoading` gates what's shown, not just the redirect
	// below. A genuine HTTP error — a status is present, including a confirmed 404 — falls
	// through instead: retrying forever behind a loader wouldn't help there, and `notFound`
	// below already decides what happens with the 404 case specifically.
	const effectiveIsLoading = isLoading || (!hasResponse && (!error || error?.status === undefined));

	useEffect(() => {
		if (feedTitle) {
			setPageTitle(feedTitle);
		}
	}, [feedTitle, setPageTitle]);

	useEffect(() => {
		if (feedSubtitle) {
			setPageSubtitle(feedSubtitle);
		}
	}, [feedSubtitle, setPageSubtitle]);

	// A feed is a 404 either because the server actively confirmed it (notFound) or because
	// a response that actually arrived came back empty. An empty `packs` on its own doesn't
	// distinguish "really empty" from "request never landed", and search opens this route
	// often enough that a transient failure is no longer a rare edge case — `error` alone
	// isn't enough, since the shared axios interceptor also rejects on a network-level
	// failure with no payload, which is exactly the case that would otherwise bounce a real
	// feed. `notFound` bypasses that caution because it's the opposite case: a confirmed
	// absence, not an unconfirmed failure.
	useEffect(() => {
		const timeout = setTimeout(() => {
			if (!effectiveIsLoading && (notFound || (!error && packs.length <= 0))) {
				routerPush('/404');
			}
		}, 500);

		return () => clearTimeout(timeout);
	}, [effectiveIsLoading, packs, error, notFound, routerPush]);

	if (!effectiveIsLoading && (notFound || (!error && packs.length <= 0))) {
		return null;
	}

	return (
		<Layout pageName={`pack-feeds-${packFeedName}`}>
			<PacksList packs={packs} isLoading={effectiveIsLoading} packFeedName={packFeedName} />
		</Layout>
	);
};

export default PacksFeed;

export const getServerSideProps: GetServerSideProps = async context => {
	const initialState = await getInitialServerState(context);

	return {
		props: {
			initialState: {
				...initialState,
				tags: [],
				theme: {
					webAppLayout: 'full',
				},
			},
		},
	};
};
